/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { IAssetDetailsResponse, IAssetInfo, IAssetTypesResponse } from "../../models/asset";
import { useFormik } from "formik";
import * as Yup from "yup";
import View from "../Container";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import { Toast } from "primereact/toast";
import Nav, { NavItem } from "../Nav";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import BankSearchSelector from "../BankSearchSelector";
import { InputNumber } from "primereact/inputnumber";
import Button from "../Button";
import { Checkbox } from "primereact/checkbox";
import Label from "../Label";
import { useHomeContext } from "../../contexts/HomeContext";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

interface ISavingAccountHandleFormProps {
    onSuccessRegister(assetInfo: ISavingAccountHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedInvestment;
}

interface ISavingAccountHandleForm {
    investedValue?: number;
    investedDate?: Date;
    negotiatingBank?: { id: string; name: string; };
    programmed?: boolean;
    recurringValue?: number;
}

const SavingAccountHandleForm = (props: ISavingAccountHandleFormProps) => {
    const [isEdit, setIsEdit] = useState(false);
    const http = useAuthHttp();
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();

    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const insertSavingAccountMutation = useMutation({
        mutationFn: async (values: ISavingAccountHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped.find(listType => listType.groupName == "Poupança")!.names[0].id!,
                asset_type: "SA",
                asset_fin_negotiating_id: values.negotiatingBank!.id!,
                asset_purchase_date: moment(values.investedDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(values.investedDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: values.investedValue!,
                    event_recurring_value: values.recurringValue,
                    event_recurring: !!values.recurringValue
                },
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {})
            };

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const updateSavingAccountMutation = useMutation({
        mutationFn: async (values: ISavingAccountHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped.find(listType => listType.groupName == "Poupança")!.names[0].id!,
                asset_type: "SA",
                asset_fin_negotiating_id: values.negotiatingBank!.id!,
                asset_purchase_date: moment(values.investedDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(values.investedDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: values.investedValue!,
                    event_recurring_value: values.recurringValue,
                    event_recurring: !!values.recurringValue
                }
            };

            return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
        }
    });

    const formik = useFormik<ISavingAccountHandleForm>({
        initialValues: {
            investedValue: 0,
            investedDate: undefined,
            negotiatingBank: undefined,
            programmed: undefined,
            recurringValue: 0
        },
        validationSchema: Yup.object().shape({
            investedDate: Yup.date().required("Campo obrigatório"),
            investedValue: Yup.number()
                .min(10, "Valor mínimo obrigatório")
                .required("Campo obrigatório"),
            negotiatingBank: Yup.object()
                .required("Campo obrigatório"),
            programmed: Yup.boolean().optional(),
            recurringValue: Yup.number()
                .when("programmed", {
                    is: true,
                    then: (schema) => schema
                        .min(10, "Valor mínimo obrigatório")
                        .required("Campo obrigatório"),
                    otherwise: (schema) => schema.optional()
                })
        }),
        onSubmit: async (values) => send(values)
    });

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                return {
                    asset_info: null
                };
            }

            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            setIsEdit(false);
            return;
        }

        setIsEdit(true);
        const eventCharge = assetDetailsQuery.data?.asset_info.asset_event_user.find(e => e.event_type == "CHARGE");
        formik.setValues({
            investedDate: moment(assetDetailsQuery.data?.asset_info.asset_purchase_date).toDate(),
            investedValue: eventCharge?.event_value,
            negotiatingBank: {
                id: assetDetailsQuery.data?.asset_info.asset_fin_issue.id,
                name: assetDetailsQuery.data?.asset_info.asset_fin_issue.inst_name
            },
            recurringValue: eventCharge?.event_recurring_value,
            programmed: !!eventCharge?.event_recurring_value
        });
    }, [assetDetailsQuery.data?.asset_info]);

    useEffect(() => {
        if (!props.initialData?.asset_sa_details) {
            return;
        }
        const data = props.initialData;
        formik.setValues({
            investedValue: data.asset_current_value || 0,
            investedDate: data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined,
            recurringValue: data.asset_sa_details?.sa_recurring_value || 0,
            programmed: (data.asset_sa_details?.sa_recurring_value || 0) > 0,
            negotiatingBank: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const send = async (values: ISavingAccountHandleForm) => {
        try {
            let assetInfo: IAssetInfo | null | undefined;
            if (isEdit) {
                assetInfo = (await updateSavingAccountMutation.mutateAsync(values))?.result;
            } else {
                assetInfo = (await insertSavingAccountMutation.mutateAsync(values))?.result;
            }

            if (assetInfo) {
                props.onSuccessRegister(values);
                await homeContext.refresh();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Poupança</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <FormField
                        id="dueDate"
                        label="Data do investimento"
                        hasError={!!formik.errors.investedDate && formik.touched.investedDate}
                        error={formik.errors.investedDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.investedDate && formik.touched.investedDate}
                            value={formik.values.investedDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("investedDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <FormField
                        id="investedValue"
                        hasError={!!formik.errors.investedValue && formik.touched.investedValue}
                        error={formik.errors.investedValue}
                        label="Valor investido"
                    >
                        <InputNumber
                            invalid={!!formik.errors.investedValue && formik.touched.investedValue}
                            value={formik.values.investedValue}
                            onValueChange={(e) =>
                                formik.setFieldValue("investedValue", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <BankSearchSelector
                        hasError={!!formik.errors.negotiatingBank && !!formik.touched.negotiatingBank}
                        error={formik.errors.negotiatingBank}
                        selected={formik.values.negotiatingBank}
                        label="Qual é o banco?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("negotiatingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <View className="align-items-center">
                        <Checkbox
                            inputId="programmed"
                            onChange={() => {
                                formik.setFieldValue("programmed", !formik.values.programmed);
                            }}
                            checked={!!formik.values.programmed}
                        />
                        <Label htmlFor="programmed" className="ml-2">Poupança programada?</Label>
                    </View>

                    {
                        formik.values.programmed && (
                            <FormField
                                id="recurringValue"
                                hasError={!!formik.errors.recurringValue && formik.touched.recurringValue}
                                error={formik.errors.recurringValue}
                                label="Valor recorrente mensal"
                            >
                                <InputNumber
                                    invalid={!!formik.errors.recurringValue && formik.touched.recurringValue}
                                    value={formik.values.recurringValue}
                                    onValueChange={(e) =>
                                        formik.setFieldValue("recurringValue", e.value)
                                    }
                                    mode="currency"
                                    currency="BRL"
                                    locale="pt-BR"
                                />
                            </FormField>
                        )
                    }

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={insertSavingAccountMutation.isPending || updateSavingAccountMutation.isPending}
                            disabled={insertSavingAccountMutation.isPending || updateSavingAccountMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default SavingAccountHandleForm;
